import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useObservable } from "react-use-observable";
import { tap } from "rxjs";

import { BUILD_NUMBER } from "../../../config";
import { IWatch } from "../../../interfaces/watch";
import { errorHandlerService } from "../../../services/errorHandler";
import { progressService } from "../../../services/progress";
import { watchService } from "../../../services/watch";
import ErrorOverlay from "./ErrorOverlay";
import MediaType from "./MediaType";
import ProcessOverlay from "./ProcessOverlay";
import { isCrossOrigin, parseUrl, userCademi, buildQueryString } from '../../../helpers/url';
import CademiScript from '../Cademi';

export interface IParams {
  tokenKey?: string; //GUID
  playerToken?: string; //JWT
}

const Watch: React.FC = () => {
  const { tokenKey, playerToken } = useParams<IParams>();
  const history = useHistory();
  const host = parseUrl(document.referrer).host;
  const user = userCademi(window.location.search);

  const [watchInfo] = useObservable<IWatch>(
    () =>
      watchService.getWatchInfo().pipe(
        tap((info) => progressService.setSessionToken(info.sessionToken)),
        tap((info) => {
          Sentry.setTag("producerAccountId", info.producerAccountId);
          Sentry.setTag("producerEduzzId", info.producerEduzzId);
          Sentry.setTag("sessionPartner", info.partner);
          Sentry.setTag("sessionKey", info.sessionKey);
          Sentry.setTag("videoKey", info.videoKey);
          Sentry.setTag("tokenKey", info.tokenKey);
          Sentry.setTag("playerVersion", BUILD_NUMBER);
        })
      ),
    []
  );

  const [currentError] = useObservable(
    () => errorHandlerService.listenCurrentError(),
    []
  );

  const defineWatchInfo = () => {
    watchService.defineWatchInfo(
      tokenKey ?? playerToken ?? '',
      host,
      user ? `${user}&` : `?${buildQueryString(window.location.search)}`
    )
  }

  const defineTokenType = () => {
    if (tokenKey) {
      watchService.setTokenType("embed");
    } else if (
      playerToken &&
      history.location.pathname.split("/")[1] === "direct"
    ) {
      watchService.setTokenType("direct");
    } else {
      watchService.setTokenType("watch");
    }
  };

  const cademiUser = () => {
    if (user) {
      history.replace({ search: user });
    }
  }

  const validate = (url?: string): boolean => {
    if (!url) return false;
    const regex = /^[a-z0-9-]+\.(cademi)\.com\.br$/;
    return regex.test(url);
  };
  const isCademi = () => {
    const crossOrigin = isCrossOrigin(document.referrer);
    return crossOrigin && validate(host);
  }
  const isvalid = isCademi();

  useEffect(cademiUser, [history, user]);
  useEffect(defineTokenType, [history, playerToken, tokenKey]);
  useEffect(defineWatchInfo, [playerToken, tokenKey, host]);

  if (currentError && currentError.type === 'api' && currentError.data === 'processing') {
    return <ProcessOverlay />;
  }

  if (currentError) {
    return (
      <ErrorOverlay
        currentErrorType={currentError.type}
        currentErrorMessage={currentError.data}
      />
    );
  }

  return (
    <>
      {isvalid && <CademiScript />}
      {watchInfo && <MediaType watchInfo={watchInfo} />}
    </>
  );
}

export default Watch;
